import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Search,
  Plus,
  Upload,
  MoreVertical,
  CheckCircle2,
  XCircle,
  UserMinus,
  Trash2,
} from "lucide-react";
import {
  listMembers,
  reactivateMember,
  deactivateMember,
  departMember,
  deleteMember,
} from "../../../../Services/adminCorporates";
import { useCorporate } from "../CorporateContext";
import StatusBadge from "../../../../Components/admin/shared/StatusBadge";
import EmptyState from "../../../../Components/admin/shared/EmptyState";
import ConfirmDialog from "../../../../Components/admin/shared/ConfirmDialog";
import AddMemberModal from "./members/AddMemberModal";
import ImportCsvModal from "./members/ImportCsvModal";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Card, CardContent } from "../../../../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../ui/select";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../../../ui/table";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../../../../ui/dropdown-menu";
import { Skeleton } from "../../../../ui/skeleton";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending_signup", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "deactivated", label: "Deactivated" },
  { value: "departed", label: "Departed" },
];

const PAGE_SIZE = 20;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch (_) {
    return "—";
  }
};

/**
 * Members tab — toolbar + paginated table + actions.
 *
 * Per §7.5 every mutation triggers a corporate detail refetch so the seat
 * counter stays in sync with the server.
 */
const Members = () => {
  const { id } = useParams();
  const { refetch: refetchCorporate } = useCorporate();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { type, member }

  // Debounced search per spec (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listMembers(id, {
        page,
        page_size: PAGE_SIZE,
        status: statusFilter === "all" ? undefined : statusFilter,
        search: debouncedSearch || undefined,
      });
      setMembers(data?.members || []);
      setTotalPages(data?.total_pages || 1);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id, page, statusFilter, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = () => {
    load();
    refetchCorporate?.();
  };

  const performAction = async (reason) => {
    if (!pendingAction) return;
    const { type, member } = pendingAction;
    try {
      if (type === "deactivate") {
        await deactivateMember(id, member.id, reason);
      } else if (type === "depart") {
        await departMember(id, member.id, reason);
      } else if (type === "reactivate") {
        await reactivateMember(id, member.id);
      } else if (type === "delete") {
        await deleteMember(id, member.id);
      }
      setPendingAction(null);
      refresh();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Action failed");
      setPendingAction(null);
    }
  };

  const renderActions = (member) => {
    const opts = [];
    if (member.status === "active") {
      opts.push(
        <DropdownMenuItem
          key="deactivate"
          onClick={() => setPendingAction({ type: "deactivate", member })}
        >
          <XCircle className="h-4 w-4 mr-2" /> Deactivate
        </DropdownMenuItem>
      );
      opts.push(
        <DropdownMenuItem
          key="depart"
          onClick={() => setPendingAction({ type: "depart", member })}
          className="text-destructive"
        >
          <UserMinus className="h-4 w-4 mr-2" /> Mark as departed
        </DropdownMenuItem>
      );
    } else if (member.status === "deactivated") {
      opts.push(
        <DropdownMenuItem
          key="reactivate"
          onClick={() => setPendingAction({ type: "reactivate", member })}
        >
          <CheckCircle2 className="h-4 w-4 mr-2" /> Reactivate
        </DropdownMenuItem>
      );
      opts.push(
        <DropdownMenuItem
          key="depart"
          onClick={() => setPendingAction({ type: "depart", member })}
          className="text-destructive"
        >
          <UserMinus className="h-4 w-4 mr-2" /> Mark as departed
        </DropdownMenuItem>
      );
    } else if (member.status === "pending_signup") {
      opts.push(
        <DropdownMenuItem
          key="delete"
          onClick={() => setPendingAction({ type: "delete", member })}
          className="text-destructive"
        >
          <Trash2 className="h-4 w-4 mr-2" /> Delete invite
        </DropdownMenuItem>
      );
    }
    if (opts.length === 0) return null;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {opts.reduce((acc, item, idx) => {
            if (idx > 0) acc.push(<DropdownMenuSeparator key={`sep-${idx}`} />);
            acc.push(item);
            return acc;
          }, [])}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search by name or email…"
            className="pl-9"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(next) => {
            setStatusFilter(next);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full md:w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImport(true)}>
            <Upload className="h-4 w-4 mr-1" /> Import CSV
          </Button>
          <Button onClick={() => setShowAdd(true)}>
            <Plus className="h-4 w-4 mr-1" /> Add member
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : members.length === 0 ? (
            <EmptyState
              title="No members yet"
              description="Add manually or import a CSV."
              action={{
                label: "Add member",
                onClick: () => setShowAdd(true),
              }}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Work email</TableHead>
                    <TableHead>Personal email</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Signed up</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">
                        {m.first_name || m.last_name
                          ? `${m.first_name || ""} ${m.last_name || ""}`.trim()
                          : "—"}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {m.work_email}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {m.personal_email}
                      </TableCell>
                      <TableCell>{m.department || "—"}</TableCell>
                      <TableCell>{m.role_title || "—"}</TableCell>
                      <TableCell>
                        <StatusBadge status={m.status} />
                      </TableCell>
                      <TableCell>
                        {m.status === "pending_signup"
                          ? "Pending"
                          : formatDate(m.signed_up_at || m.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        {renderActions(m)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AddMemberModal
        open={showAdd}
        onOpenChange={setShowAdd}
        corporateId={id}
        onCreated={refresh}
      />

      <ImportCsvModal
        open={showImport}
        onOpenChange={setShowImport}
        corporateId={id}
        onImported={refresh}
      />

      {/* Action confirms */}
      <ConfirmDialog
        open={pendingAction?.type === "deactivate"}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title="Deactivate member?"
        description="This will immediately remove the member's access to corporate features. They can be reactivated later."
        confirmLabel="Deactivate"
        destructive
        withReason
        reasonLabel="Reason (optional)"
        onConfirm={performAction}
      />
      <ConfirmDialog
        open={pendingAction?.type === "depart"}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title="Mark as departed?"
        description="This is permanent. The member's corporate access will be revoked but their Aris account will remain. Their seat will be returned to the pool."
        confirmLabel="Mark as departed"
        destructive
        withReason
        reasonLabel="Reason (optional)"
        onConfirm={performAction}
      />
      <ConfirmDialog
        open={pendingAction?.type === "reactivate"}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title="Reactivate member?"
        description="The member will regain access to all corporate features."
        confirmLabel="Reactivate"
        onConfirm={performAction}
      />
      <ConfirmDialog
        open={pendingAction?.type === "delete"}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title="Delete invite?"
        description="The pending invitation will be removed. This cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={performAction}
      />
    </div>
  );
};

export default Members;
